import { useEffect, useState, useCallback } from 'react'
import { Phone, Sparkles, Video, FileText, ChevronRight, X } from 'lucide-react'
import { callHistoryApi, historyApi } from '../../services/api'
import type { CallHistoryItem, HistorySessionDetail } from '../../services/historyTypes'
import SessionDetail from '../voice/history/SessionDetail'
import { EmptyState } from '../patterns/EmptyState'

function groupByDay(items: CallHistoryItem[]): { day: string; items: CallHistoryItem[] }[] {
  const out: { day: string; items: CallHistoryItem[] }[] = []
  for (const it of items) {
    const day = it.startedAt.slice(0, 10)
    const last = out[out.length - 1]
    if (last && last.day === day) last.items.push(it)
    else out.push({ day, items: [it] })
  }
  return out
}

function dayLabel(day: string): string {
  const today = new Date().toISOString().slice(0, 10)
  const yest = new Date(Date.now() - 864e5).toISOString().slice(0, 10)
  if (day === today) return 'Today'
  if (day === yest) return 'Yesterday'
  return new Date(day + 'T00:00:00Z').toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })
}

function durationLabel(sec: number): string {
  if (sec < 60) return `${sec}s`
  const m = Math.floor(sec / 60)
  return m < 60 ? `${m}m` : `${Math.floor(m / 60)}h ${m % 60}m`
}

export default function CallHistoryDashboard() {
  const [items, setItems] = useState<CallHistoryItem[]>([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState<CallHistoryItem | null>(null)
  const [detail, setDetail] = useState<HistorySessionDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)

  useEffect(() => {
    let alive = true
    callHistoryApi.mine({ limit: 50 })
      .then((r) => { if (alive) setItems(r.data.items) })
      .catch(() => { if (alive) setItems([]) })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [])

  const open = useCallback((it: CallHistoryItem) => {
    setSelected(it)
    setDetail(null)
    // Voice calls reuse the participant-gated session-detail endpoint; DM/PSTN
    // calls go through the unified /call-history/item endpoint. Both return the
    // HistorySessionDetail shape, so SessionDetail renders either unchanged.
    const fetchDetail =
      it.source === 'voice'
        ? (it.sessionId ? historyApi.session(it.sessionId) : null)
        : callHistoryApi.item(it.source, it.id)
    if (fetchDetail) {
      setDetailLoading(true)
      fetchDetail
        .then((r) => setDetail(r.data))
        .catch(() => setDetail(null))
        .finally(() => setDetailLoading(false))
    }
  }, [])

  const groups = groupByDay(items)

  return (
    <div className="h-full flex">
      <div className="flex-1 min-w-0 overflow-y-auto p-6">
        {loading ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : items.length === 0 ? (
          <EmptyState size="md" title="No calls yet" description="Calls you take part in will show up here — with their transcript, chat, and recordings." />
        ) : (
          <div className="space-y-6 max-w-3xl">
            {groups.map((g) => (
              <div key={g.day}>
                <div className="text-xs font-semibold text-muted-foreground mb-2">{dayLabel(g.day)}</div>
                <div className="space-y-1">
                  {g.items.map((it) => (
                    <button
                      key={`${it.source}:${it.id}`}
                      onClick={() => open(it)}
                      className={`w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg border border-border bg-card hover:bg-muted transition ${selected && selected.id === it.id ? 'ring-1 ring-primary' : ''}`}
                    >
                      <Phone size={16} className="text-muted-foreground shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-foreground truncate">{it.title}</div>
                        <div className="text-xs text-muted-foreground truncate">
                          {it.counterpartyLabel} · {new Date(it.startedAt).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })} · {durationLabel(it.durationSec)}
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0 text-muted-foreground">
                        {it.hasTranscript && <FileText size={14} aria-label="Transcript" />}
                        {it.hasRecording && <Video size={14} aria-label="Recording" />}
                        {it.hasSummary && <Sparkles size={14} aria-label="AI summary" />}
                        <ChevronRight size={14} />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {selected && (
        <div className="w-[460px] shrink-0 border-l border-border bg-background overflow-y-auto">
          <div className="h-14 px-4 flex items-center justify-between border-b border-border bg-card">
            <div className="text-sm font-semibold text-foreground truncate">{selected.title}</div>
            <button onClick={() => setSelected(null)} className="p-1 rounded-md hover:bg-muted text-muted-foreground"><X size={16} /></button>
          </div>
          {detailLoading ? (
            <div className="p-6 text-sm text-muted-foreground">Loading call…</div>
          ) : detail ? (
            <SessionDetail
              detail={detail}
              filters={{}}
              onFilters={() => {}}
              isHost={false}
              vocab={[]}
              channelId={detail.session.channelId}
              onChanged={() => {}}
            />
          ) : (
            <div className="p-6">
              <EmptyState size="sm" title="No detail available" description="This call has no stored transcript, chat, or recording." />
            </div>
          )}
        </div>
      )}
    </div>
  )
}
